<template>
  <v-card class="main-card">
    <v-card-title class="screen-title">
      <span>障害/メンテナンス情報一覧</span>
      <manual-button path="/nwinfo-infomation/" />
    </v-card-title>
    <v-card-text class="pa-2" v-if="isLoaded">
      <v-row class="pa-2">
        <v-col cols="2">
          <app-label-group
            label="最終更新日時"
            :value="
              $filter.datetime(failureInfo.lastUpdateDateTime, { s: true })
            "
          ></app-label-group>
        </v-col>
        <v-btn
          class="updateBtn"
          color="primary"
          @click="update"
          :disabled="failureInfo.failureStatus === 'UPDATING'"
          >{{
            failureInfo.failureStatus === "UPDATING" ? "更新中" : "情報更新"
          }}</v-btn
        >
      </v-row>

      <v-row no-gutters>
        <v-col>
          <app-search
            :search="maintenance.search"
            v-model="maintenance.filter"
          />
        </v-col>
        <div class="d-flex flex-row ga-1 flex-wrap ml-2">
          <v-btn color="primary" @click="downloadCsv">一覧ダウンロード</v-btn>
        </div>
      </v-row>

      <div>
        <app-table
          v-model:table-sort-by="maintenance.sortBy"
          primary-key="number"
          :fields="maintenance.fields"
          :items="items"
          :filter="maintenance.filter"
          :filter-option="maintenance.filter_option"
          :hover="true"
          @click:row="handleRowClicked"
          :loading="!failureList"
          sticky-header="45vh"
        >
          <template #head(vpnVnCode)="data">
            <span>接続先<br />VPN/VNコード </span>
            <v-icon
              class="v-data-table-header__sort-icon"
              :icon="data.getSortIcon(data.field)"
            />
          </template>

          <template #cell(vnConnectName)="{ value }">
            {{ $filter.emptyTo(value, "-") }}
          </template>
          <template #cell(enumber)="{ value }">
            {{ $filter.emptyTo(value, "-") }}
          </template>
          <template #cell(vpnVnCode)="{ value }">
            {{ $filter.emptyTo(value, "-") }}
          </template>
          <template #cell(vlan)="{ value }">
            {{ $filter.emptyTo(value, "-") }}
          </template>
        </app-table>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type {
  CommonFailure,
  FailureListResponse,
  FailureUpdateInformation,
} from "@/apis/InformationApi";
import FailureMaintenanceReference from "@/modals/failureMaintenance/FailureMaintenanceReference.vue";
import type { SearchOption } from "@/components/AppSearch.vue";

export default defineComponent({
  name: "FailureMaintenanceList",
  data() {
    return {
      maintenance: {
        sortBy: {
          key: "number",
          order: "asc",
        } as SortItem,
        fields: [
          {
            key: "number",
            title: "回線番号",
            sortable: true,
            width: "125px",
          },
          {
            key: "failure",
            title: "障害情報",
            sortable: true,
            sortByFormatted: true,
            width: "110px",
            filterByFormatted: true,
            formatter: (v: FailureListEntity["failure"]) => {
              return v ? "有り" : "無し";
            },
          },
          {
            key: "maintenance",
            title: "メンテナンス情報",
            sortable: true,
            sortByFormatted: true,
            width: "115px",
            filterByFormatted: true,
            formatter: (v: FailureListEntity["maintenance"]) => {
              return v ? "有り" : "無し";
            },
          },
          {
            key: "service",
            title: "サービス",
            sortable: true,
            sortByFormatted: true,
            width: "220px",
            formatter: (value: string) =>
              this.$filter.optionText(value, serviceItems),
          },
          {
            key: "vnConnectName",
            title: "VNコネクト名",
            sortable: true,
          },
          {
            key: "enumber",
            title: "紐づけE番号",
            sortable: true,
            width: "130px",
          },
          {
            key: "vpnVnCode",
            title: "接続先VPN/VNコード",
            sortable: true,
            width: "150px",
          },
          {
            key: "vlan",
            title: "VLAN",
            align: "end",
            sortByFormatted: true,
            width: "70px",
            filterByFormatted: true,
            formatter: (_v: string, _k: string, item: FailureListEntity) => {
              if (item.vlanIdType) {
                switch (item.vlanIdType) {
                  case "SPECIFIED":
                    return item.vlanId;
                  case "OTHER":
                  case "UNTAG":
                    return this.$filter.enumTo(item.vlanIdType, "vlanIdType");
                }
              } else {
                return null;
              }
            },
          },
        ] as AppTableHeader<FailureListEntity>[],
        items: [] as FailureListEntity[],
        search: {
          number: {
            type: "text",
            label: "回線番号",
          },
          failure: {
            type: "select",
            label: "障害情報",
            items: [
              { value: "有り", text: "有り" },
              { value: "無し", text: "無し" },
            ],
          },
          maintenance: {
            type: "select",
            label: "メンテナンス情報",
            items: [
              { value: "有り", text: "有り" },
              { value: "無し", text: "無し" },
            ],
          },
          service: {
            type: "select",
            label: "サービス",
            items: serviceItems,
          },
          vnConnectName: {
            type: "text",
            label: "VNコネクト名",
          },
          enumber: {
            type: "text",
            label: "紐づけE番号",
          },
          vpnVnCode: {
            type: "text",
            label: "接続先VPN/VNコード",
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          number: "like",
          failure: "equal",
          maintenance: "equal",
          service: "equal",
          vnConnectName: "like",
          enumber: "like",
          vpnVnCode: "like",
        } as FilterOption,
      },
      failureInfo: {} as FailureUpdateInformation,
      /** 障害情報一覧。nullの場合は取得中（テーブルはloading表示） */
      failureList: null as FailureListResponse | null,
      isLoaded: false,
      /** ポーリング情報. nullの場合はポーリングしてない */
      pooling: null as number | NodeJS.Timeout | null,
    };
  },
  computed: {
    /** 一覧表示データ */
    items(): FailureListEntity[] {
      if (this.failureList) {
        return [
          // VNコネクト情報
          ...this.failureList.vnConnectList.map<FailureListEntity>((e) => ({
            number: e.wnumber,
            failure: e.failure,
            maintenance: e.maintenance,
            service: "VN",
            vnConnectName: e.vnConnectName,
            enumber: e.enumber,
            vpnVnCode: e.vpnVnCode,
            vlanIdType: e.vlanIdType,
            vlanId: e.vlanId,
          })),
          // IPsec回線
          ...this.failureList.ipsecList.map<FailureListEntity>((e) => ({
            number: e.xnumber,
            failure: e.failure,
            maintenance: e.maintenance,
            service: "IPSEC",
            vnConnectName: null,
            enumber: e.enumber,
            vpnVnCode: null,
            vlanIdType: null,
            vlanId: null,
          })),
          // CPA 5G SA
          ...this.failureList.cpaList.map<FailureListEntity>((e) => ({
            number: `${this.$filter.emptyTo(e.wnumber?.wnumberAct, "-")}(${
              e.domain
            })`,
            failure: e.failure,
            maintenance: e.maintenance,
            service: "CPA_5GSA",
            vnConnectName: null,
            enumber: null,
            vpnVnCode: e.vpnVnCode,
            vlanIdType: null,
            vlanId: null,
          })),
          // VNL2/L3コネクト
          ...this.failureList.vnl2l3ConnectList.map<FailureListEntity>((e) => ({
            number: e.wnumber,
            failure: e.failure,
            maintenance: e.maintenance,
            service: "VNL2L3",
            vnConnectName: e.vnConnectName,
            enumber: e.enumber,
            vpnVnCode: null,
            vlanIdType: e.l3VlanType,
            vlanId: e.l3VlanId,
          })),
          // マルチクラウド
          ...this.failureList.cloudList.map<FailureListEntity>((e) => ({
            number: e.wnumber,
            failure: e.failure,
            maintenance: e.maintenance,
            service: "MULTICLOUD",
            vnConnectName: e.vnConnectName,
            enumber: e.enumber,
            vpnVnCode: e.vpnVnCode,
            vlanIdType: null,
            vlanId: null,
          })),
          // TYPE4インターネット
          ...this.failureList.type4List.map<FailureListEntity>((e) => ({
            number: e.wnumber,
            failure: e.failure,
            maintenance: e.maintenance,
            service: "TYPE4",
            vnConnectName: e.vnConnectName,
            enumber: null,
            vpnVnCode: e.vpnVnCode,
            vlanIdType: null,
            vlanId: null,
          })),
          // SASEゲートウェイ
          ...this.failureList.saseGatewayList.map<FailureListEntity>((e) => ({
            number: e.wnumber,
            failure: e.failure,
            maintenance: e.maintenance,
            service: "SASE_GATEWAY",
            vnConnectName: e.vnConnectName,
            enumber: null,
            vpnVnCode: e.vpnVnCode,
            vlanIdType: null,
            vlanId: null,
          })),
        ];
      }
      return [];
    },
  },
  async mounted() {
    this.failureInfo = await this.$api.information.getFailureInformation();
    this.isLoaded = true;
    // メンテナンス情報一覧は別のローディング表示となるためisLoaded=trueの後
    await this.loadFailureList();
    // 更新中の場合はポーリングを起動
    if (this.failureInfo.failureStatus === "UPDATING") {
      await this.poolFailure();
    }
  },
  unmounted() {
    if (this.pooling) {
      clearInterval(this.pooling);
    }
  },
  methods: {
    /** 障害情報一覧取得。取得中はテーブルをloading表示 */
    async loadFailureList() {
      this.failureList = null;
      this.failureList = await this.$api.information.getFailureList(
        undefined,
        false
      );
    },

    /** 障害情報の取得が完了するまで30秒毎に状態を確認。取得完了後に障害情報一覧を取得 */
    async poolFailure() {
      if (this.failureInfo.failureStatus === "UPDATING") {
        // 取得が完了するまで30秒毎に状態を確認。取得完了後に障害情報一覧を取得
        this.pooling = setInterval(async () => {
          this.failureInfo =
            await this.$api.information.getFailureInformation(false);
          if (this.failureInfo.failureStatus === "COMPLETED") {
            clearInterval(this.pooling!);
            this.pooling = null;
            await this.loadFailureList();
          }
        }, 30 * 1000);
      } else {
        // 取得完了しているので障害一覧を取得
        await this.loadFailureList();
      }
    },
    /** 最新の障害情報の取得を指示して取得が完了したら障害情報一覧を取得 */
    async update() {
      // 最新の障害情報の取得を指示
      await this.$api.information.updateFailureInformation({
        failureKeyId: this.failureInfo.failureKeyId,
      });

      this.failureInfo = await this.$api.information.getFailureInformation();
      await this.poolFailure();
    },

    /** フロント側のみでのCSVダウンロード */
    downloadCsv() {
      // 障害情報とメンテナンス情報はフォーマット不要
      const csvFields = (
        [...this.maintenance.fields] as { title: string; key: string }[]
      ).map((v) => {
        if (v.key === "failure" || v.key === "maintenance") {
          return { title: v.title, key: v.key };
        } else {
          return v;
        }
      });

      this.$service.csvService.appTableCsvDownLoad(
        "failure_maintenance",
        this.items.sortBy(["number", "asc"]),
        this.$service.csvService.genFieldList(csvFields)
      );
    },

    /** 行選択した障害/メンテナンス詳細情報画面を表示 */
    handleRowClicked($event: FailureListEntity) {
      this.$modal.show(FailureMaintenanceReference, {
        failureMaintenanceItem: $event,
      });
    },
  },
});

/**サービス表示マッピング用 */
export const serviceItems = [
  { value: "VN", text: "VNコネクト" },
  { value: "IPSEC", text: "IPsec" },
  { value: "VNL2L3", text: "VN L2/L3コネクト" },
  { value: "CPA_5GSA", text: "CPA 5G SA" },
  { value: "MULTICLOUD", text: "マルチクラウドGW" },
  { value: "TYPE4", text: "インターネットType4" },
  { value: "SASE_GATEWAY", text: "SASEゲートウェイ" },
];

/** 障害メンテナンス情報一覧情報表示用インターフェース */
type FailureListEntity = Pick<CommonFailure, "maintenance" | "failure"> & {
  /** 回線番号 */
  number: string;
  /** サービス種別 */
  service:
    | "VN"
    | "IPSEC"
    | "VNL2L3"
    | "CPA_5GSA"
    | "MULTICLOUD"
    | "TYPE4"
    | "SASE_GATEWAY";
  /** VNコネクト名 */
  vnConnectName: string | null;
  /** 紐づけE番号 */
  enumber: string | null;
  /** 接続先VPN/VNコード */
  vpnVnCode: string | null;
  /** VLAN IDのType指定 */
  vlanIdType: "UNTAG" | "OTHER" | "SPECIFIED" | null;
  /** VLAN ID */
  vlanId: number | null;
};
</script>

<style lang="scss" scoped>
.updateBtn {
  margin: 0.5em 0 1.5em 3em;
}
</style>
